import { DateTime } from 'luxon'

export const getDecimalCount = (min: number, num: number) => {
  const normalized = Number(num.toPrecision(12))
  const numStr =
    normalized % 1 === 0 ? normalized.toFixed(1) : normalized.toString()
  return Math.max(min, numStr.includes('.') ? numStr.split('.')[1].length : 0)
}

export const convertFertilizerAmountRatioToSqmBase = (
  area: string,
  unit: string
) => {
  const parsedArea = parseFloat(area)
  switch (unit) {
    case 'rai':
      return parsedArea / 1600.0
    case 'ha':
      return parsedArea / 10000.0
    case '10a':
      return parsedArea / 1000.0
    case 'ac':
      return parsedArea / 4046.856
    default:
      return parsedArea
  }
}

export const convertFertilizerAmountSqmToUnit = (
  area: number,
  unit: string
) => {
  let res: number
  switch (unit) {
    case 'rai':
      res = area * 1600.0
      break
    case 'ha':
      res = area * 10000.0
      break
    case '10a':
      res = area * 1000.0
      break
    case 'ac':
      res = area * 4046.856
      break
    default:
      res = area
      break
  }
  return res.toFixed(getDecimalCount(2, res))
}

export const computeSeedAmountPerUnitArea = (
  seedValue: number,
  areaSqm: number | string,
  areaUnit: string,
) => {
  const numerator = seedValue;
  let denominator: number;
  let res: number;
  let precision: number = 2;
  const parsedArea = typeof areaSqm === 'string' ? parseFloat(areaSqm) : areaSqm;
  
  switch (areaUnit) {
    case 'rai': 
      res = parsedArea / 1600.0;
      precision = 3;
      denominator = res;
      break;
    case 'ha':
      res = parsedArea / 10000.0;
      precision = 4;
      denominator = res;
      break;
    case '10a': 
      res = parsedArea / 1000.0;
      precision = 3;
      denominator = res;
      break;
    case 'ac':
      res = parsedArea / 4046.856;
      precision = 4;
      denominator = res;
      break;
    default:
      res = parsedArea;
      denominator = res;
      break;
  }
  return (numerator / denominator).toFixed(precision);
};

// area stored as m2
export const convertSqmToAreaByUnit = (
  areaSqm: number | string,
  unit: string,
  options?: { minDecimals?: number; computed?: boolean }
) => {
  const min = options?.minDecimals || 2
  const computed = options?.computed || false

  let res: number
  const parsedArea = typeof areaSqm === 'string' ? parseFloat(areaSqm) : areaSqm
  switch (unit) {
    case 'rai':
      res = parsedArea / 1600.0
      if (computed) return res.toFixed(3)
      break
    case 'ha':
      res = parsedArea / 10000.0
      if (computed) return res.toFixed(3)
      break
    case '10a':
      res = parsedArea / 1000.0
      if (computed) return res.toFixed(1)
      break
    case 'ac':
      res = parsedArea / 4046.856
      if (computed) return res.toFixed(3)
      break
    default:
      res = parsedArea
      if (computed) return res.toFixed(0)
      break
  }
  return res.toFixed(getDecimalCount(min, res))
}

export const convertSqmToYieldByUnit = (
  yieldSqm: number | string,
  unit: string,
  options?: { minDecimals?: number; computed?: boolean }
) => {
  const min = options?.minDecimals || 2
  const computed = options?.computed || false

  let res: number
  const parsedArea =
    typeof yieldSqm === 'string' ? parseFloat(yieldSqm) : yieldSqm

  switch (unit) {
    case 'rai':
      res = parsedArea * 1600.0
      if (computed) return res.toFixed(3)
      break
    case 'ha':
      res = parsedArea * 10000.0
      if (computed) return res.toFixed(3)
      break
    case '10a':
      res = parsedArea * 1000.0
      if (computed) return res.toFixed(3)
      break
    case 'ac':
      res = parsedArea * 4046.856
      if (computed) return res.toFixed(3)
      break
    default:
      res = parsedArea
      if (computed) return res.toFixed(6)
      break
  }
  return res.toFixed(getDecimalCount(min, res))
}

export const convertAreaByUnitToSqm = (area: string | number, unit: string) => {
  const parsedArea = typeof area === 'string' ? parseFloat(area) : area
  switch (unit) {
    case 'rai':
      return parsedArea * 1600.0
    case 'ha':
      return parsedArea * 10000.0
    case '10a':
      return parsedArea * 1000.0
    case 'ac':
      return parsedArea * 4046.856
    default:
      return parsedArea
  }
}

export const formatIncomeUnits = (unit: string) =>
  ({
    baht: 'baht',
    satang: 'baht', // The frontend should always show `baht` even with 'cents'. The values are converted.
  }[unit])

// To prevent javascript floating point calculation issues we truncate and remove the comma instead of just doing * 100.
export const formatIncomeForBackend = (value: string) =>
  Number(parseFloat(value.replace(/\s/g, '')).toFixed(2).replace('.', ''))

export const formatLatLon = (coords: number[]) =>
  coords.map((x) => x.toFixed(5))

export const displayWeight = (
  weight: number,
  options?: { minDec?: number; computed?: boolean }
) => {
  const min = options?.minDec || 2
  const computed = options?.computed || false
  return weight.toFixed(computed ? 3 : getDecimalCount(min, weight))
}

// Similar to rails activesupport present?
export const isPresent = <T>(value: T | undefined | null): value is T =>
  value !== undefined && value !== null && value.toString().trim() != ''

export const luxonUserLocale = (lang: string) => {
  switch (lang) {
    case 'th':
      return 'th-TH'
    case 'vi':
      return 'vi-VN'
    case 'ja':
      return 'ja-JP'
    default:
      return 'en-US'
  }
}

export const LFDateFormat = {
  weatherDate: (seconds: number, lang: string) =>
    DateTime.fromSeconds(seconds)
      .setLocale(luxonUserLocale(lang))
      .toLocaleString({ ...DateTime.DATE_SHORT, weekday: 'short' }),
  // This method is used to convert a datetime string to a timezoned date before sending to backend
  datetimeFromInputWithTimezone: (
    datetime: string,
    zone: string,
    lang: string
  ) =>
    DateTime.fromFormat(datetime, 'yyyy/MM/dd HH:mm', { zone: zone })
      .minus({ years: lang == 'th' ? 543 : 0 })
      .toISO(),
  dateFromInputWithTimezone: (datetime: string, zone: string, lang: string) =>
    DateTime.fromFormat(datetime, 'yyyy/MM/dd', { zone: zone })
      .minus({ years: lang == 'th' ? 543 : 0 })
      .toISO(),
  dateValidityFromFormat: (datetime: string, format: string) =>
    DateTime.fromFormat(datetime, format).isValid,
  datetimeFromISO: (datetime: string, zone: string, lang: string) =>
    DateTime.fromISO(
      datetime,
      lang == 'th' ? { outputCalendar: 'Buddhist' } : {}
    )
      .setZone(zone)
      .setLocale(luxonUserLocale(lang)),
  datetimeFromFormat: (
    datetime: string,
    zone: string,
    lang: string,
    format: string
  ) => {
    if (!datetime) return
    return DateTime.fromFormat(datetime, format, {
      zone: zone,
    }).minus({ years: lang == 'th' ? 543 : 0 })
  },
  timezoneFormat: (
    datetime: string,
    zone: string,
    lang: string,
    format: string
  ) =>
    DateTime.fromISO(datetime)
      .setZone(zone)
      .reconfigure({
        outputCalendar: lang === 'th' ? 'Buddhist' : 'gregory',
      })
      .setLocale(luxonUserLocale(lang))
      .toFormat(format),
  localFormat: (datetime: string, lang: string, format: string) =>
    DateTime.fromISO(datetime)
      .reconfigure({
        outputCalendar: lang === 'th' ? 'Buddhist' : 'gregory',
      })
      .setLocale(luxonUserLocale(lang))
      .toFormat(format),
}

export const dateSortValidation = (a?: DateTime, b?: DateTime) => ({
  first: a?.isValid ? a : null,
  second: b?.isValid ? b : null,
})

export enum DateFormats {
  DATE_SHORT = 'yyyy/MM/dd',
  DATE_MED = 'DD',
  DATETIME_SHORT = 'yyyy/MM/dd HH:mm',
  DATETIME_MED = 'ff',
  DATE_HUMAN = 'DDDD',
}
